<template>
    <div class="module">
        <header class="module__header--with-disclaimer">
            <a
                class="back-btn"
                @click="goBack('admin-relays')"
            >
                <span class="icon-chevron-left" />
            </a>
            <h1 class="has-back-btn">Edit Relay</h1>
        </header>

        <section
            v-if="!isLoading"
            class="module__body"
        >
            <form @submit.prevent="updateRelay">
                <div
                    class="form-group form-group-header"
                    data-test-id="relay-details-header"
                >
                    {{ relayTypes[type].label }} Details
                </div>
                <div class="form-group">
                    <label
                        for="site-name-textbox"
                        data-test-id="site-name-title"
                    >
                        Name
                    </label>
                    <input
                        id="site-name-textbox"
                        v-model="site_name"
                        data-test-id="site-name-textbox"
                        maxlength="191"
                        minlength="3"
                        required
                        type="text"
                    />
                </div>

                <div
                    v-if="isAdvancedRelay"
                    class="form-group"
                >
                    <slider-toggle-field
                        label="SmartEdge Enabled"
                        description="Turn this on if this Advanced Relay includes a SmartEdge device."
                        :value="isSmartDeviceEnabled"
                        :isLocked="true"
                        :disabled="true"
                        data-test-id="smartedge-enabled-toggle"
                    ></slider-toggle-field>
                </div>

                <div class="form-group">
                    <label data-test-id="relay-tower-title">Mounted on Tower?</label>
                    <div
                        class="radio-options"
                        data-test-id="radio-options"
                    >
                        <div class="radio-option">
                            <RadioButton
                                v-model="is_tower_mounted"
                                name="is_tower_mounted"
                                pt:input:required="true"
                                :value="true"
                                variant="filled"
                                inputId="relay-tower-yes"
                                data-test-id="relay-tower-yes"
                            />
                            <label
                                for="relay-tower-yes"
                                data-test-id="relay-tower-yes-text"
                                >Yes</label
                            >
                        </div>
                        <div class="radio-option">
                            <RadioButton
                                v-model="is_tower_mounted"
                                name="is_tower_mounted"
                                pt:input:required="true"
                                :value="false"
                                variant="filled"
                                inputId="relay-tower-no"
                                data-test-id="relay-tower-no"
                            />
                            <label
                                for="relay-tower-no"
                                data-test-id="relay-tower-no-text"
                                >No</label
                            >
                        </div>
                    </div>
                </div>
                <div class="form-group">
                    <label
                        for="customer-asset-textbox"
                        data-test-id="customer-asset-title"
                        >Customer Asset #</label
                    >
                    <input
                        id="customer-asset-textbox"
                        v-model="asset_number"
                        data-test-id="customer-asset-textbox"
                        maxlength="191"
                        minlength="3"
                        type="text"
                    />
                </div>
                <div
                    v-if="isEdgeRelay || isAdvancedRelay"
                    class="form-group"
                >
                    <label
                        for="relay–serial-number-textbox"
                        data-test-id="relay–serial-number-title"
                        >{{ relayTypes.EDGE.label }} Serial #</label
                    >
                    <input
                        id="relay–serial-number-textbox"
                        v-model="relay_serial_number"
                        data-test-id="relay–serial-number-textbox"
                        required
                        minlength="3"
                        maxlength="191"
                        type="text"
                    />
                </div>

                <div class="form-group">
                    <span class="label-with-tooltip">
                        <label
                            for="relay–install-textbox"
                            data-test-id="relay–install-title"
                            >Install Date
                        </label>
                        <info-tooltip>Choose an installation date before today</info-tooltip>
                    </span>
                    <input
                        id="relay–install-textbox"
                        v-model="installedAt"
                        data-test-id="relay–install-textbox"
                        :max="maxInstalledAt"
                        type="datetime-local"
                        step="1"
                        @focus="handleInstalledAtFocus"
                    />
                    <div class="help"><span class="label">Time Zone: </span><span>UTC</span></div>
                </div>
                <div class="form-group">
                    <label
                        for="relay–installer-textbox"
                        data-test-id="relay–installer-title"
                        >Installer Name</label
                    >
                    <input
                        id="relay–installer-textbox"
                        v-model="installed_by"
                        data-test-id="relay–installer-textbox"
                        maxlength="191"
                        minlength="3"
                        type="text"
                    />
                </div>
                <div class="form-group lat-long-container no-clearfix">
                    <div>
                        <label
                            for="relay–latitude-textbox"
                            data-test-id="relay–latitude-title"
                            >Latitude</label
                        >
                        <input
                            id="relay–latitude-textbox"
                            v-model="latitude"
                            data-test-id="relay–latitude-textbox"
                            class="short"
                            type="number"
                            min="-90"
                            max="90"
                            step="any"
                        />
                    </div>
                    <div>
                        <label
                            for="relay–longitude-textbox"
                            data-test-id="relay–longitude-title"
                            >Longitude</label
                        >
                        <input
                            id="relay–longitude-textbox"
                            v-model="longitude"
                            data-test-id="relay–longitude-textbox"
                            class="short"
                            type="number"
                            min="-180"
                            max="180"
                            step="any"
                        />
                    </div>
                </div>
                <div class="form-group no-clearfix">
                    <label
                        for="relay–elevation-textbox"
                        data-test-id="relay–elevation-title"
                        >Elevation</label
                    >
                    <div>
                        <input
                            id="relay–elevation-textbox"
                            v-model="altitude"
                            data-test-id="relay–elevation-textbox"
                            type="number"
                            step="any"
                        />
                        <div class="help">Meters</div>
                    </div>
                </div>
                <div class="form-group">
                    <label
                        for="relay-description-textbox"
                        data-test-id="relay-description-title"
                        >Description</label
                    >
                    <textarea
                        id="relay-description-textbox"
                        v-model="description"
                        class="short"
                        data-test-id="relay-description-textbox"
                        minlength="3"
                        maxlength="500"
                    />
                </div>
                <div
                    v-if="isEdgeRelay || isAdvancedRelay"
                    class="form-group form-group-header"
                >
                    Power
                </div>
                <div
                    class="form-group"
                    data-test-id="relay-power-type-select"
                >
                    <label
                        for="relay-power-type-textbox"
                        data-test-id="relay-power-type-title"
                        >Type</label
                    >
                    <select
                        id="relay-power-type-textbox"
                        v-model="power_type"
                        data-test-id="relay-power-type-textbox"
                        required
                    >
                        <option
                            v-if="power_type === null"
                            :value="null"
                            data-test-id="relay-power-type-select-null"
                        ></option>
                        <option
                            v-for="powerType in sortedRelayPowerTypes"
                            :key="powerType.type"
                            :value="`${powerType.type}`"
                            :data-test-id="`relay-power-type-select-${powerType.type.toLowerCase()}`"
                        >
                            {{ powerType.label }}
                        </option>
                    </select>
                </div>
                <template v-if="power_type === relayPowerTypes.SOLAR.type">
                    <div class="form-group">
                        <label
                            for="relay-panel-textbox"
                            data-test-id="relay-panel-title"
                            >Panel</label
                        >
                        <input
                            id="relay-panel-textbox"
                            v-model="solar_power_panel_type"
                            data-test-id="relay-panel-textbox"
                            maxlength="50"
                            type="text"
                        />
                    </div>

                    <div class="form-group">
                        <label
                            for="relay-wattage-textbox"
                            data-test-id="relay-wattage-title"
                            >Wattage</label
                        >
                        <input
                            id="relay-wattage-textbox"
                            v-model="solar_power_wattage"
                            data-test-id="relay-wattage-textbox"
                            type="number"
                            class="hide-controls"
                            min=".0001"
                            max="4294967295"
                            step=".0001"
                        />
                        <div class="help">Max 4 decimal places</div>
                    </div>
                </template>
                <template v-if="power_type === relayPowerTypes.AC.type">
                    <div
                        class="form-group"
                        data-test-id="relay-power-source-field"
                    >
                        <label data-test-id="relay-power-source-title">Source</label>
                        <div
                            class="radio-options"
                            data-test-id="relay-power-source-options"
                        >
                            <div
                                v-for="powerSource in relayAcPowerSources"
                                :key="powerSource.type"
                                class="radio-option"
                            >
                                <RadioButton
                                    v-model="ac_power_source_type"
                                    name="ac_power_source_type"
                                    :value="`${powerSource.type}`"
                                    variant="filled"
                                    :inputId="`relay-power-source-${powerSource.type}`"
                                    :data-test-id="`relay-power-source-${powerSource.type}`"
                                />
                                <label
                                    :for="`relay-power-source-${powerSource.type}`"
                                    :data-test-id="`relay-power-source-${powerSource.type}=text`"
                                    >{{ powerSource.label }}</label
                                >
                            </div>
                        </div>
                    </div>
                    <div
                        class="form-group"
                        data-test-id="relay-voltage-field"
                    >
                        <label data-test-id="relay-voltage-title">Voltage</label>
                        <div
                            class="radio-options"
                            data-test-id="relay-voltage-options"
                        >
                            <div
                                v-for="powerVoltage in relayAcPowerVoltages"
                                :key="powerVoltage.type"
                                class="radio-option"
                            >
                                <RadioButton
                                    v-model="ac_power_voltage"
                                    name="ac_power_voltage"
                                    :value="`${powerVoltage.type}`"
                                    variant="filled"
                                    :inputId="`relay-voltage-${powerVoltage.type}`"
                                    :data-test-id="`relay-voltage-${powerVoltage.type}`"
                                />
                                <label
                                    :for="`relay-voltage-${powerVoltage.type}`"
                                    :data-test-id="`relay-voltage-${powerVoltage.type}=text`"
                                    >{{ powerVoltage.label }}</label
                                >
                            </div>
                        </div>
                    </div>
                    <div class="form-group">
                        <label
                            for="relay-circuitamps-textbox"
                            data-test-id="relay-circuitamps-title"
                            >Circuit Amps</label
                        >
                        <input
                            id="relay-circuitamps-textbox"
                            v-model="ac_power_circuit_amps"
                            data-test-id="relay-circuitamps-textbox"
                            type="number"
                            class="hide-controls"
                            min=".0001"
                            max="4294967295"
                            step=".0001"
                        />
                        <div class="help">Max 4 decimal places</div>
                    </div>
                </template>
                <template v-if="power_type === relayPowerTypes.BATTERY.type && isHastyRelay">
                    <div class="form-group">
                        <label data-test-id="relay-battery-is-external-title">Is Battery External?</label>
                        <div
                            class="radio-options"
                            data-test-id="relay-battery-is-external-options"
                        >
                            <div class="radio-option">
                                <RadioButton
                                    v-model="is_battery_external"
                                    name="is_battery_external"
                                    pt:input:required="true"
                                    :value="true"
                                    variant="filled"
                                    inputId="relay-battery-external-yes"
                                    data-test-id="relay-battery-external-yes"
                                />
                                <label
                                    for="relay-battery-external-yes"
                                    data-test-id="relay-battery-external-yes-text"
                                    >Yes</label
                                >
                            </div>
                            <div class="radio-option">
                                <RadioButton
                                    v-model="is_battery_external"
                                    name="is_battery_external"
                                    pt:input:required="true"
                                    :value="false"
                                    variant="filled"
                                    inputId="relay-battery-external-no"
                                    data-test-id="relay-battery-external-no"
                                />
                                <label
                                    for="relay-battery-external-no"
                                    data-test-id="relay-battery-external-no-text"
                                    >No</label
                                >
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="is_battery_external"
                        class="form-group"
                    >
                        <label
                            for="relay-battery-type-textbox"
                            data-test-id="relay-battery-type-title"
                            >Battery Type</label
                        >
                        <input
                            id="relay-battery-type-textbox"
                            v-model="battery_type"
                            data-test-id="relay-battery-type-textbox"
                            maxlength="50"
                            type="text"
                        />
                    </div>
                </template>

                <relay-radios
                    v-model="radios"
                    :max-radios="maxRadios"
                    :current-assembly-id="assemblyId"
                />

                <div
                    class="form-group form-group-header"
                    data-test-id="image-upload-title"
                >
                    Image Upload
                </div>

                <div class="form-group form-group-wide">
                    <RelayImageUpload
                        :existingImages="existing_images"
                        @file-removed="handleFileRemoved"
                        @file-uploaded="handleFileUploaded"
                        @uploading="handleUploading"
                        @uploading-complete="handleUploadingComplete"
                        @cover-image-set="handleCoverImageChanged"
                    />
                </div>
                <form-errors :response="errorResponse"></form-errors>

                <div class="form-group">
                    <button
                        :class="['btn', 'btn-primary', { active: submitted && !imagesUploading }]"
                        data-test-id="cta-save"
                        type="submit"
                    >
                        <div>Update</div>
                    </button>
                </div>
            </form>
        </section>
    </div>
</template>

<script>
import RadioButton from 'primevue/radiobutton'
import { isNumber } from 'lodash'
import FormErrors from '@/components/FormErrors.vue'
import RelayImageUpload from '@/components/RelayImageUpload.vue'
import canToast from '@/mixins/canToast'
import hasBackButton from '@/mixins/hasBackButton'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import relayTypes from '@/constants/relay-types'
import radioTypes from '@/constants/radio-types'
import relayPowerTypes, { ordered as sortedRelayPowerTypes } from '@/constants/relay-power-types'
import { ordered as relayAcPowerSources } from '@/constants/relay-ac-power-sources'
import { ordered as relayAcPowerVoltages } from '@/constants/relay-ac-power-voltages'
import InfoTooltip from '@/components/InfoTooltip.vue'
import powerFields from '@/mixins/powerFields'
import RelayRadios from '@/components/RelayRadios.vue'
import SliderToggleField from '@/components/SliderToggleField.vue'

export default {
    components: {
        FormErrors,
        RadioButton,
        RelayImageUpload,
        InfoTooltip,
        RelayRadios,
        SliderToggleField,
    },

    mixins: [canToast, hasBackButton, needsOrganizationSlug, powerFields],

    data() {
        return {
            isLoading: true,
            id: null,
            type: null, // Must default to null or powerFields watcher breaks things
            site_name: null,
            asset_number: null,
            relay_serial_number: null,
            installed_at: null,
            installed_by: null,
            latitude: null,
            longitude: null,
            altitude: null,
            description: null,
            radios: [],
            assemblyId: null,
            relayTypes,
            relayPowerTypes,
            sortedRelayPowerTypes,
            relayAcPowerSources,
            relayAcPowerVoltages,
            is_tower_mounted: null,
            errorResponse: null,
            submitted: false,
            existing_images: [],
            new_images: [],
            imagesUploading: false,
            maxInstalledAt: null,
            coverImageId: null,
            coverImageUuid: null,
            radioTypes,
            isSmartDeviceEnabled: true,
        }
    },

    computed: {
        // Omitting a removed radio is what detaches it on save.
        radiosForSubmission() {
            return this.radios.filter((radio) => !radio.ui_removed).map(({ urn, name }) => ({ urn, name }))
        },

        maxRadios() {
            return this.isHastyRelay ? 1 : 2
        },

        isEdgeRelay() {
            return this.type === relayTypes.EDGE.type
        },

        isHastyRelay() {
            return this.type === relayTypes.HASTY.type
        },

        isAdvancedRelay() {
            return this.type === relayTypes.ADVANCED.type
        },

        /**
         * Sometimes, the HTML native datetime-local component can store
         * timestamps without seconds (HH:mm). This ensures that the
         * value we store always includes seconds (HH:mm:ss).
         */
        installedAt: {
            get() {
                return this.installed_at
            },
            set(value) {
                if (value) {
                    this.installed_at = window.moment(value).format('YYYY-MM-DDTHH:mm:ss')
                } else {
                    this.installed_at = null
                }
            },
        },
    },

    watch: {
        type(newValue) {
            if (newValue === relayTypes.HASTY.type) {
                this.relay_serial_number = null

                this.radios = this.radios.slice(0, 1)
            }
        },
    },

    async mounted() {
        await this.fetchModel(this.organizationSlug, this.$route.params.relayId, 'relays')
    },

    methods: {
        handleInstalledAtFocus() {
            this.maxInstalledAt = window.moment().utc().format('YYYY-MM-DDTHH:mm:ss')
            if (this.installed_at === null) {
                this.installed_at = window.moment().startOf('day').hour(0).format('YYYY-MM-DDTHH:mm:ss')
            }
        },
        updateRelay() {
            this.submitted = true

            this.$http
                .put(`/${this.organizationSlug}/relays/${this.id}`, {
                    site_name: this.site_name,
                    asset_number: this.asset_number,
                    relay_serial_number: this.relay_serial_number,
                    installed_at: this.installed_at,
                    installed_by: this.installed_by,
                    latitude: this.latitude,
                    longitude: this.longitude,
                    altitude: this.altitude,
                    description: this.description,
                    is_tower_mounted: this.is_tower_mounted,
                    power_type: this.power_type,
                    solar_power_panel_type: this.solar_power_panel_type,
                    solar_power_wattage: this.solar_power_wattage,
                    ac_power_circuit_amps: this.ac_power_circuit_amps,
                    ac_power_source_type: this.ac_power_source_type,
                    ac_power_voltage: this.ac_power_voltage,
                    is_battery_external: this.is_battery_external,
                    battery_type: this.battery_type,
                    radios: this.radiosForSubmission,
                    new_images: this.new_images,
                    existing_images: this.existing_images.map(({ id }) => id),
                    cover_image_id: this.coverImageId,
                    cover_image_uuid: this.coverImageUuid,
                    is_smart_device_enabled: this.isSmartDeviceEnabled,
                })
                .then(() => {
                    this.toast('Relay details have been updated successfully!')
                    this.$router.push(`/${this.organizationSlug}/relays`)
                })
                .catch((error) => {
                    this.submitted = false
                    this.errorResponse = error.response
                })
        },

        fetchModel(organizationSlug, id, type) {
            return this.$http.get(`/${organizationSlug}/${type}/${id}`).then(({ data }) => {
                this.id = data.id
                this.type = data.type
                this.site_name = data.site_name
                this.asset_number = data.asset_number
                this.relay_serial_number = data.serial_number
                this.installed_at = data.installed_at
                this.installed_by = data.installed_by
                this.latitude = data.latitude
                this.longitude = data.longitude
                this.altitude = data.altitude
                this.description = data.description
                this.is_tower_mounted = data.is_tower_mounted
                this.power_type = data.power_type
                this.solar_power_panel_type = data.solar_power_panel_type
                this.solar_power_wattage = data.solar_power_wattage
                this.ac_power_circuit_amps = data.ac_power_circuit_amps
                this.ac_power_source_type = data.ac_power_source_type
                this.ac_power_voltage = data.ac_power_voltage
                this.battery_type = data.battery_type
                this.is_battery_external = data.is_battery_external

                this.installed_at = null
                if (data.installed_at) {
                    const installedAt = window.moment(data.installed_at)
                    this.installed_at = installedAt.format('YYYY-MM-DDTHH:mm:ss')
                }

                this.assemblyId = data?.assembly?.id ?? null

                if (data?.assembly?.members_with_asset) {
                    const assemblyRadios = data.assembly.members_with_asset
                        .filter((member) => member.asset !== null && member.asset_type === 'asset_radio')
                        .map((member) => ({
                            urn: member.asset.urn,
                            name: member.label,
                            serial_number: member.asset.serial_number,
                            icon: member.asset.icon_path ?? null,
                            ui_removed: false,
                            ui_original: true,
                        }))
                    if (assemblyRadios.length > 0) {
                        this.radios = assemblyRadios
                    }
                    this.isSmartDeviceEnabled = data.assembly.members_with_asset.some(
                        (asset) => asset.asset_type === 'asset_smart_device'
                    )
                }

                let images = []
                if (data.images) {
                    images = data.images.map((image) => ({
                        id: image.id,
                        url: image.thumbnail_url,
                        is_cover_image: image.is_cover_image,
                    }))
                }

                this.existing_images = images

                this.isLoading = false
            })
        },

        handleFileRemoved(deletedId) {
            let index = this.new_images.findIndex((imageId) => imageId === deletedId)

            if (index > -1) {
                this.new_images.splice(index, 1)
            } else {
                index = this.existing_images.findIndex(({ id }) => id === deletedId)
                if (index > -1) {
                    this.existing_images.splice(index, 1)
                }
            }
        },

        handleFileUploaded(uploadedId) {
            this.new_images.push(uploadedId)
        },

        handleUploading() {
            this.imagesUploading = true
        },

        handleUploadingComplete() {
            this.imagesUploading = false
        },

        handleCoverImageChanged(newCoverImageId) {
            if (isNumber(newCoverImageId)) {
                this.coverImageId = newCoverImageId
                this.coverImageUuid = null
            } else {
                this.coverImageId = null
                this.coverImageUuid = newCoverImageId
            }
        },
    },
}
</script>
<style scoped lang="scss">
.form-group-header {
    font-size: 15px;
    font-weight: 800;
    font-family: Avenir, Helvetica, Arial, sans-serif;
    height: 24px;
}

.no-clearfix {
    &:after {
        display: none;
    }
}

.lat-long-container {
    display: flex;
    justify-content: space-between;
}

.form-group-wide {
    width: 545px;
}

.radio-options {
    display: flex;
    flex-direction: row;
    gap: 1.5rem;

    .radio-option {
        display: flex;
        gap: 0.5em;
        align-items: center;

        label {
            margin: 0;
        }
    }
}

input[type='number'].short {
    /* number doesn't respect width 100% */
    width: 190px;
}

input[type='number'] {
    /* number doesn't respect width 100% */
    width: 420px;
}

.label-with-tooltip {
    display: flex;
    align-items: flex-start;

    label {
        width: auto;
    }
}
</style>
